export const CLIENT_SLOTCARS_INTERPOLATION_MS = 70;

export function midpoint(left, right) {
  return {
    x: (left.x + right.x) / 2,
    y: (left.y + right.y) / 2,
  };
}

export function sampleQuadraticPoint(start, control, end, t) {
  const inverse = 1 - t;
  return {
    x: (inverse * inverse * start.x) + (2 * inverse * t * control.x) + (t * t * end.x),
    y: (inverse * inverse * start.y) + (2 * inverse * t * control.y) + (t * t * end.y),
  };
}

export function buildSampledClosedPath(points, samplesPerCurve = 16) {
  if (!Array.isArray(points) || points.length < 3) return Array.isArray(points) ? [...points] : [];
  const sampled = [];
  let start = midpoint(points[points.length - 1], points[0]);
  sampled.push(start);

  for (let index = 0; index < points.length; index += 1) {
    const control = points[index];
    const end = midpoint(points[index], points[(index + 1) % points.length]);
    const stepCount = Math.max(4, samplesPerCurve);
    for (let step = 1; step <= stepCount; step += 1) {
      const t = step / stepCount;
      sampled.push(sampleQuadraticPoint(start, control, end, t));
    }
    start = end;
  }

  return sampled;
}

export function buildTrackMetrics(points) {
  const safePoints = buildSampledClosedPath(Array.isArray(points) ? points : [], 18);
  const segments = [];
  let totalLength = 0;

  for (let index = 0; index < safePoints.length; index += 1) {
    const from = safePoints[index];
    const to = safePoints[(index + 1) % safePoints.length];
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy);
    segments.push({
      from,
      to,
      dx,
      dy,
      length,
      startLength: totalLength,
      endLength: totalLength + length,
    });
    totalLength += length;
  }

  return {
    points: safePoints,
    segments,
    totalLength,
  };
}

export function buildSmoothClosedPath(points) {
  if (!Array.isArray(points) || points.length < 2) return '';
  let path = '';
  for (let index = 0; index < points.length; index += 1) {
    const point = points[index];
    path += `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y} `;
  }
  return `${path}Z`;
}

export function normalizeProgress(value) {
  return ((Number(value) || 0) % 1 + 1) % 1;
}

export function sampleTrackPosition(metrics, progress, lateralOffset = 0) {
  if (!metrics || !metrics.totalLength || !Array.isArray(metrics.segments) || metrics.segments.length === 0) {
    return { x: 0, y: 0, angle: 0 };
  }

  const normalized = normalizeProgress(progress);
  const targetLength = normalized * metrics.totalLength;
  const segment = metrics.segments.find((item) => targetLength <= item.endLength) || metrics.segments[metrics.segments.length - 1];
  const localLength = targetLength - segment.startLength;
  const ratio = segment.length > 0 ? localLength / segment.length : 0;
  const baseX = segment.from.x + (segment.dx * ratio);
  const baseY = segment.from.y + (segment.dy * ratio);
  const normalX = segment.length > 0 ? -segment.dy / segment.length : 0;
  const normalY = segment.length > 0 ? segment.dx / segment.length : 0;

  return {
    x: baseX + (normalX * lateralOffset),
    y: baseY + (normalY * lateralOffset),
    angle: Math.atan2(segment.dy, segment.dx) * (180 / Math.PI),
  };
}

export function getNearestProgressToPoint(metrics, point) {
  if (!metrics || !metrics.totalLength || !Array.isArray(metrics.segments) || metrics.segments.length === 0 || !point) {
    return 0;
  }

  let bestDistance = Number.POSITIVE_INFINITY;
  let bestProgress = 0;

  metrics.segments.forEach((segment) => {
    const lengthSquared = (segment.dx * segment.dx) + (segment.dy * segment.dy);
    const rawProjection = lengthSquared > 0
      ? (((point.x - segment.from.x) * segment.dx) + ((point.y - segment.from.y) * segment.dy)) / lengthSquared
      : 0;
    const projection = Math.max(0, Math.min(1, rawProjection));
    const projectedX = segment.from.x + (segment.dx * projection);
    const projectedY = segment.from.y + (segment.dy * projection);
    const distance = Math.hypot(point.x - projectedX, point.y - projectedY);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestProgress = normalizeProgress((segment.startLength + (segment.length * projection)) / metrics.totalLength);
    }
  });

  return bestProgress;
}

export function getWrappedRangeLength(start, end) {
  const normalizedStart = normalizeProgress(start);
  const normalizedEnd = normalizeProgress(end);
  return normalizedStart <= normalizedEnd
    ? normalizedEnd - normalizedStart
    : (1 - normalizedStart) + normalizedEnd;
}

export function buildProgressSegmentPath(metrics, start, end, sampleCount = 24) {
  if (!metrics || !metrics.totalLength) return '';
  const span = getWrappedRangeLength(start, end);
  const steps = Math.max(2, sampleCount);
  const points = [];
  for (let index = 0; index <= steps; index += 1) {
    const ratio = index / steps;
    const progress = normalizeProgress(start + (span * ratio));
    points.push(sampleTrackPosition(metrics, progress, 0));
  }
  if (points.length === 0) return '';
  return points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
}

export function getWrappedMidpoint(start, end) {
  const normalizedStart = normalizeProgress(start);
  const span = getWrappedRangeLength(start, end);
  return normalizeProgress(normalizedStart + (span / 2));
}

export function findCornerForProgress(corners, progress) {
  if (!Array.isArray(corners)) return null;
  const normalized = normalizeProgress(progress);
  return corners.find((corner) => {
    if (corner.start <= corner.end) {
      return normalized >= corner.start && normalized <= corner.end;
    }
    return normalized >= corner.start || normalized <= corner.end;
  }) || null;
}

export function getTurnSign(metrics, progress) {
  if (!metrics || !metrics.totalLength || !Array.isArray(metrics.segments) || metrics.segments.length < 2) return 0;
  const targetLength = normalizeProgress(progress) * metrics.totalLength;
  const currentIndex = Math.max(0, metrics.segments.findIndex((segment) => targetLength <= segment.endLength));
  const previous = metrics.segments[(currentIndex - 1 + metrics.segments.length) % metrics.segments.length];
  const current = metrics.segments[currentIndex];
  const cross = (previous.dx * current.dy) - (previous.dy * current.dx);
  if (Math.abs(cross) < 0.0001) return 0;
  return cross > 0 ? 1 : -1;
}

function smoothstep(edge0, edge1, value) {
  if (edge0 === edge1) return value >= edge1 ? 1 : 0;
  const ratio = Math.max(0, Math.min(1, (value - edge0) / (edge1 - edge0)));
  return ratio * ratio * (3 - (2 * ratio));
}

function getWrappedProgressDelta(fromProgress, toProgress) {
  return normalizeProgress(toProgress - fromProgress);
}

export function getRacingLineOffset(metrics, track, progress, car) {
  const corner = findCornerForProgress(track?.corners, progress);
  let offset = 0;
  if (corner) {
    const span = getWrappedRangeLength(corner.start, corner.end);
    const progressIntoCorner = getWrappedProgressDelta(corner.start, progress);
    const normalizedProgress = Math.max(0, Math.min(1, progressIntoCorner / Math.max(0.0001, span)));
    const bell = Math.sin(normalizedProgress * Math.PI);
    const rampSize = Math.min(0.28, Math.max(0.14, span * 0.75));
    const entryRamp = smoothstep(0, rampSize, normalizedProgress);
    const exitRamp = 1 - smoothstep(1 - rampSize, 1, normalizedProgress);
    const envelope = Math.max(0, Math.min(1, entryRamp * exitRamp));
    const midpointProgress = getWrappedMidpoint(corner.start, corner.end);
    const turnSign = getTurnSign(metrics, midpointProgress) || getTurnSign(metrics, progress);
    const apexStrength = (track?.laneWidth || 40) * Math.min(0.18, (corner.apexStrength || 0.22) * 0.72);
    offset += turnSign * apexStrength * bell * envelope;
  }
  if (car?.slipping) {
    const slipSign = car.slipDirection || (corner ? -(getTurnSign(metrics, progress) || 0) : 0);
    const slipAmount = (track?.laneWidth || 40) * (0.12 + ((car.slipSeverity || 0.35) * 0.16));
    offset -= slipSign * slipAmount;
  }
  return offset;
}

export function interpolateWrappedProgress(fromProgress, toProgress, ratio) {
  const from = normalizeProgress(fromProgress);
  const to = normalizeProgress(toProgress);
  let delta = to - from;
  if (delta > 0.5) delta -= 1;
  if (delta < -0.5) delta += 1;
  return normalizeProgress(from + (delta * ratio));
}